import { capture } from '@parcel/analytics/browser';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@parcel/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouterState } from '@tanstack/react-router';
import { BookOpen, Check, Copy, ExternalLink, KeyRound, Plus, Trash2 } from 'lucide-react';
import * as React from 'react';
import {
  type ApiKeySummary,
  DEVELOPER_DOCS_URL,
  createApiKey,
  formatApiKeyHint,
  formatApiKeyTimestamp,
  listApiKeys,
  revokeApiKey,
} from '../../lib/api-keys.js';
import { ApiError, apiErrorCode } from '../../lib/api.js';
import { useEntitlements } from '../../lib/entitlements.js';
import { getWorkspace, getWorkspaceSlugFromPath } from '../../lib/workspaces.js';
import { LockedFeaturePanel } from '../billing/locked-feature-panel.js';
import { SettingsPageHeader, SettingsShell } from './settings-shell.js';
import { TableSkeletonRows } from './settings-skeletons.js';

export function ApiKeysTable({
  keys,
  onRevoke,
}: {
  keys: ApiKeySummary[];
  onRevoke: (key: ApiKeySummary) => void;
}) {
  if (keys.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed px-6 py-12 text-center">
        <KeyRound className="size-8 text-muted-foreground" aria-hidden />
        <p className="text-sm font-medium">No API keys yet</p>
        <p className="max-w-sm text-sm text-muted-foreground">
          Create a key to give scripts and integrations read access to this workspace's data.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[220px]">Name</TableHead>
            <TableHead className="w-[140px]">Key</TableHead>
            <TableHead className="w-[90px]">Scope</TableHead>
            <TableHead className="w-[120px]">Created</TableHead>
            <TableHead className="w-[120px]">Last used</TableHead>
            <TableHead className="w-[48px]">
              <span className="sr-only">Actions</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {keys.map((key) => (
            <TableRow key={key.id}>
              <TableCell className="max-w-[220px] truncate font-medium" title={key.name}>
                {key.name}
              </TableCell>
              <TableCell className="font-mono text-xs text-muted-foreground">
                {formatApiKeyHint(key.last4)}
              </TableCell>
              <TableCell>
                <Badge variant="secondary">Read</Badge>
              </TableCell>
              <TableCell className="text-muted-foreground">
                {formatApiKeyTimestamp(key.createdAt)}
              </TableCell>
              <TableCell className="text-muted-foreground">
                {formatApiKeyTimestamp(key.lastUsedAt)}
              </TableCell>
              <TableCell>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Revoke API key"
                  onClick={() => onRevoke(key)}
                >
                  <Trash2 className="size-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function ApiKeySecretReveal({
  secret,
  copied,
  onCopy,
}: {
  secret: string;
  copied: boolean;
  onCopy: () => void;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 rounded-md border bg-muted/40 p-3">
        <code className="min-w-0 flex-1 break-all font-mono text-xs">{secret}</code>
        <Button variant="outline" size="icon" aria-label="Copy API key" onClick={onCopy}>
          {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
        </Button>
      </div>
      <p className="text-sm text-amber-600 dark:text-amber-500">
        You won't be able to see this key again. Store it somewhere safe — treat it like a password.
      </p>
    </div>
  );
}

export function ApiKeysSettingsPage() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const workspaceSlug = getWorkspaceSlugFromPath(pathname);
  const { entitlements } = useEntitlements(workspaceSlug);
  const queryClient = useQueryClient();

  const workspaceQuery = useQuery({
    queryKey: ['workspace', workspaceSlug],
    enabled: Boolean(workspaceSlug),
    queryFn: () => getWorkspace(workspaceSlug ?? ''),
  });
  const canManage = workspaceQuery.data?.capabilities.canManageApiKeys ?? false;

  const keysQuery = useQuery({
    queryKey: ['api-keys', workspaceSlug],
    enabled: Boolean(workspaceSlug) && canManage,
    queryFn: () => listApiKeys(workspaceSlug ?? ''),
  });

  const [createOpen, setCreateOpen] = React.useState(false);
  const [name, setName] = React.useState('');
  const [creating, setCreating] = React.useState(false);
  const [createError, setCreateError] = React.useState<string | null>(null);
  const [createdSecret, setCreatedSecret] = React.useState<string | null>(null);
  const [copied, setCopied] = React.useState(false);
  const [revokeTarget, setRevokeTarget] = React.useState<ApiKeySummary | null>(null);
  const [revoking, setRevoking] = React.useState(false);
  const [revokeError, setRevokeError] = React.useState<string | null>(null);

  const refetchKeys = () =>
    queryClient.invalidateQueries({ queryKey: ['api-keys', workspaceSlug] });

  const handleCreate = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!workspaceSlug || !name.trim()) return;
    setCreating(true);
    setCreateError(null);
    try {
      const result = await createApiKey(workspaceSlug, { name: name.trim() });
      capture('api_key_created', {});
      setCreatedSecret(result.secret);
      setName('');
      void refetchKeys();
    } catch (err) {
      const code = apiErrorCode(err);
      setCreateError(
        code === 'plan_required'
          ? 'API access is a Pro feature. Upgrade to Pro to create API keys.'
          : err instanceof ApiError && typeof err.body === 'object' && err.body
            ? ((err.body as { error?: { message?: string } }).error?.message ??
              'Unable to create the API key.')
            : 'Unable to create the API key.',
      );
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async () => {
    if (!createdSecret) return;
    await navigator.clipboard.writeText(createdSecret);
    setCopied(true);
  };

  const closeCreateDialog = () => {
    setCreateOpen(false);
    setCreatedSecret(null);
    setCopied(false);
    setCreateError(null);
    setName('');
  };

  const handleRevoke = async () => {
    if (!workspaceSlug || !revokeTarget) return;
    setRevoking(true);
    setRevokeError(null);
    try {
      await revokeApiKey(workspaceSlug, revokeTarget.id);
      setRevokeTarget(null);
      void refetchKeys();
    } catch (err) {
      setRevokeError(err instanceof Error ? err.message : 'Unable to revoke the API key.');
    } finally {
      setRevoking(false);
    }
  };

  const showAdminGate = workspaceQuery.isSuccess && !canManage;
  // Hold the page shape until the plan and the key list are known, so a Pro workspace never
  // flashes the upgrade panel or "No API keys yet" on the way in.
  const loadingKeys =
    Boolean(workspaceSlug) && (workspaceQuery.isPending || (canManage && keysQuery.isPending));

  return (
    <SettingsShell>
      <SettingsPageHeader
        eyebrow="Workspace"
        title="API Keys"
        description="Programmatic read access to your workspace's data. Treat API keys like passwords — never share them publicly."
      />

      {loadingKeys ? (
        <div className="space-y-6" data-testid="api-keys-skeleton">
          <Skeleton className="h-[74px] w-full rounded-lg" />
          <div className="flex items-center justify-between">
            <Skeleton className="h-4 w-36" />
            <Skeleton className="h-9 w-28" />
          </div>
          <div className="overflow-hidden rounded-lg border">
            <Table>
              <TableBody>
                <TableSkeletonRows columns={6} rows={2} />
              </TableBody>
            </Table>
          </div>
        </div>
      ) : showAdminGate ? (
        <div className="rounded-lg border px-6 py-12 text-center text-sm text-muted-foreground">
          Only workspace admins can manage API keys.
        </div>
      ) : workspaceSlug && entitlements.apiCreditCeiling24h === 0 ? (
        <LockedFeaturePanel
          feature="API access"
          gate="api_keys"
          requiredPlan="pro"
          benefit="Create API keys to query your Parcel data programmatically."
          workspaceSlug={workspaceSlug}
          ctaLabel="Upgrade to Pro"
        />
      ) : (
        <div className="space-y-6">
          <a
            href={DEVELOPER_DOCS_URL}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-3 rounded-lg border p-4 transition-colors hover:bg-muted/50"
          >
            <BookOpen className="size-5 text-muted-foreground" aria-hidden />
            <span className="flex-1">
              <span className="block text-sm font-medium">Developer docs</span>
              <span className="block text-sm text-muted-foreground">
                API reference for programmatic access to Parcel.
              </span>
            </span>
            <ExternalLink className="size-4 text-muted-foreground" aria-hidden />
          </a>

          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-muted-foreground">Workspace API keys</h3>
            <Button onClick={() => setCreateOpen(true)} disabled={!workspaceSlug}>
              <Plus className="size-4" />
              Create key
            </Button>
          </div>

          <ApiKeysTable keys={keysQuery.data ?? []} onRevoke={setRevokeTarget} />
        </div>
      )}

      <Dialog
        open={createOpen}
        onOpenChange={(open) => (open ? setCreateOpen(true) : closeCreateDialog())}
      >
        <DialogContent>
          {createdSecret ? (
            <>
              <DialogHeader>
                <DialogTitle>API key created</DialogTitle>
                <DialogDescription>Copy your new key now and store it securely.</DialogDescription>
              </DialogHeader>
              <ApiKeySecretReveal secret={createdSecret} copied={copied} onCopy={handleCopy} />
              <div className="flex justify-end gap-2">
                <Button onClick={closeCreateDialog}>Done</Button>
              </div>
            </>
          ) : (
            <form onSubmit={handleCreate} className="space-y-4">
              <DialogHeader>
                <DialogTitle>Create API key</DialogTitle>
                <DialogDescription>
                  The key will have read access to this workspace's data.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2">
                <Label htmlFor="api-key-name">Name</Label>
                <Input
                  id="api-key-name"
                  value={name}
                  maxLength={64}
                  placeholder="e.g. CRM sync"
                  onChange={(event) => setName(event.target.value)}
                  autoFocus
                />
              </div>
              <div className="flex items-center justify-between rounded-md border p-3 text-sm">
                <span className="text-muted-foreground">Scope</span>
                <Badge variant="secondary">Read</Badge>
              </div>
              {createError ? <p className="text-sm text-destructive">{createError}</p> : null}
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={closeCreateDialog}>
                  Cancel
                </Button>
                <Button type="submit" disabled={creating || !name.trim()}>
                  {creating ? 'Creating…' : 'Create key'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(revokeTarget)}
        onOpenChange={(open) => {
          if (!open) {
            setRevokeTarget(null);
            setRevokeError(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke API key</DialogTitle>
            <DialogDescription>
              Revoke "{revokeTarget?.name}"? Integrations using this key will stop working
              immediately. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {revokeError ? <p className="text-sm text-destructive">{revokeError}</p> : null}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setRevokeTarget(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleRevoke} disabled={revoking}>
              {revoking ? 'Revoking…' : 'Revoke key'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </SettingsShell>
  );
}
